import {
  GridModalFormProps,
  UseFormReturnType,
  Validator,
  globalMessageHandler,
  loadMultipleOptions,
  useForm,
} from "common";
import { useParams } from "common/components/Form/ParamsContext";
import { Dictionary } from "features/dictionaries/dictionaryApi";
import useDictionaries from "features/dictionaries/useDictionaries";
import { getPartnerGroupService } from "features/partner-groups/partnerGroupApi";
import { getUserService } from "features/users/userApi";
import I18n from "i18n";
import { PartnerCommissionItem } from "partner-commissions/partnerCommissionApi";
import { Params } from "react-router-dom";
import { PartnerItem, PartnerOptions, getPartnerService } from "./partnerApi";
import { upsertPartnerCommission } from "./partnerCommissionState";

export interface UsePartnerFormProps extends GridModalFormProps {}

type RouteParams = {
  partner_id?: string;
};

export interface UsePartnerFormReturnType extends UseFormReturnType<
  PartnerItem,
  PartnerOptions
> {
  params?: Readonly<
    Params<
      "partner_id" | "partner_group_id" | "booking_id" | "booking_option_id"
    >
  >;
  dictionaries: Dictionary;
  syncPartnerCommission: (commission: PartnerCommissionItem) => void;
}

export default function usePartnerForm({
  id,
  afterSave,
  defaultValues,
}: UsePartnerFormProps): UsePartnerFormReturnType {
  const params = useParams<
    "partner_id" | "partner_group_id" | "booking_id" | "booking_option_id"
  >();
  const scope = { "partner-groups": params.partner_group_id };
  const service = getPartnerService(scope);

  function customValidation(newItem: PartnerItem) {
    const commissions = (newItem as any).partner_commissions || [];
    const coupons = (newItem as any).coupons || [];
    const isCreate = !params.partner_id;
    const rowErrors: Record<number, any> = {};
    const couponRowErrors: Record<number, any> = {};
    let baseCommissionError: any = undefined;

    coupons.forEach((coupon: any, index: number) => {
      const hasCode = !!String(coupon?.code || "").trim();
      const hasDiscount =
        coupon?.discount_percent === 0 ||
        coupon?.discount_percent === "0" ||
        (!!coupon?.discount_percent &&
          String(coupon?.discount_percent).trim() !== "");

      if (!hasCode) {
        couponRowErrors[index] = {
          ...(couponRowErrors[index] || {}),
          code: "Kuponkód kötelező",
        };
      }

      if (hasCode && !hasDiscount) {
        couponRowErrors[index] = {
          ...(couponRowErrors[index] || {}),
          discount_percent: "Kedvezmény százalék kötelező",
        };
      }
    });

    if (!commissions.length) {
      return {
        partner_commissions: {
          message:
            "Hiányzik az alapértelmezett jutalék sor (program és dátum nélkül, Létszám: 1)",
        },
        ...(Object.keys(couponRowErrors).length > 0
          ? {
              coupons: {
                message:
                  "Ha kupon sort felviszel, a kuponkód kötelező. Kód esetén a kedvezmény százalék is kötelező (0 is megadható).",
                ...couponRowErrors,
              },
            }
          : {}),
      };
    }

    // Check for base commission row in create mode
    if (isCreate) {
      const hasBaseCommission = commissions.some(
        (commission: any) =>
          (commission.program_id === null ||
            commission.program_id === undefined) &&
          (commission.valid_from === null ||
            commission.valid_from === undefined) &&
          Number(commission.headcount) === 1,
      );

      if (!hasBaseCommission) {
        baseCommissionError = {
          partner_commissions: {
            message:
              "Hiányzik az alapértelmezett jutalék sor (program és dátum nélkül, Létszám: 1)",
          },
        };
      }
    }

    commissions.forEach((commission: any, index: number) => {
      // Skip validation for base row in create mode
      const isBaseRow =
        isCreate &&
        (commission.program_id === null ||
          commission.program_id === undefined) &&
        (commission.valid_from === null ||
          commission.valid_from === undefined) &&
        Number(commission.headcount) === 1;

      if (isBaseRow) {
        // Only validate commission_rate for base row
        if (!commission.commission_rate) {
          rowErrors[index] = {
            commission_rate: "Number is required",
          };
        }
        return;
      }

      // For non-base rows, validate all fields
      const result = Validator.validate(
        {
          //valid_from: Validator.rule.string().required(),
          //program_id: Validator.rule.number().required(),
          headcount: Validator.rule.number().required(),
          commission_rate: Validator.rule.number().required(),
        },
        commission,
        "PartnerCommission",
      );

      if (!result.success) {
        rowErrors[index] = result.errors;
      }
    });

    if (Object.keys(rowErrors).length > 0) {
      return {
        partner_commissions: {
          message: baseCommissionError
            ? "Hiányzik az alapértelmezett jutalék sor (program és dátum nélkül, Létszám: 1)"
            : undefined,
          ...rowErrors,
        },
        ...(Object.keys(couponRowErrors).length > 0
          ? {
              coupons: {
                message:
                  "Ha kupon sort felviszel, a kuponkód kötelező. Kód esetén a kedvezmény százalék is kötelező (0 is megadható).",
                ...couponRowErrors,
              },
            }
          : {}),
      };
    }

    if (Object.keys(couponRowErrors).length > 0) {
      return {
        coupons: {
          message:
            "Ha kupon sort felviszel, a kuponkód kötelező. Kód esetén a kedvezmény százalék is kötelező (0 is megadható).",
          ...couponRowErrors,
        },
        ...baseCommissionError,
      };
    }

    return baseCommissionError;
  }

  const form = useForm<PartnerItem, PartnerOptions>({
    id: params.partner_id || id,
    ignoreNavigateAfterCreate: true,
    translationCategory: "Partner",
    rules: {
      name: Validator.rule.string().required(),
      email: Validator.rule.string().required(),
      phone: Validator.rule.string().required(),
      partner_group_id: params.partner_group_id
        ? Validator.rule.optional()
        : Validator.rule.any().required(),
    },
    afterSave,
    defaultValues,
    customValidation,
    onValidationFailed: () => {
      globalMessageHandler.snack({
        severity: "error",
        message: I18n.t("App.formHasErrors"),
      });
    },
    ...service,
    loadOptions: loadMultipleOptions({
      partner_group_id: () =>
        getPartnerGroupService(undefined).loadAutocomplete(),
      account_manager_id: () => getUserService().loadAutocomplete(),
    }),
  });
  const { dictionaries } = useDictionaries();

  function syncPartnerCommission(commission: PartnerCommissionItem) {
    const commissions = form.item.partner_commissions || [];
    const nextCommissions = upsertPartnerCommission(commissions, commission);

    form.setAttributes({ partner_commissions: nextCommissions }, true);
    const { partner_commissions, ...remainingErrors } = form.errors;
    form.setErrors(remainingErrors);
  }

  return { ...form, params, dictionaries, syncPartnerCommission };
}
